import {
  Box,
  ChevronDownIcon,
  ChevronRightIcon,
  IconButton,
  Row,
  TextWithTooltip,
} from "@hightouch/htui";

export type FolderRailItemProps = {
  label: string;
  depth: number;
  isSelected: boolean;
  onSelect: () => void;
  /** Shows the disclosure chevron. Without a toggle handler nothing renders. */
  hasChildren?: boolean;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
};

/**
 * One folder row in a folder rail. Two sibling buttons rather than one: the
 * disclosure chevron and the label do different things, and a button cannot
 * hold another button. The chevron slot is reserved even without a chevron so
 * labels at one depth line up.
 */
export const FolderRailItem = ({
  label,
  depth,
  isSelected,
  onSelect,
  hasChildren = false,
  isExpanded = false,
  onToggleExpanded,
}: FolderRailItemProps) => (
  <Row
    width="100%"
    minWidth={0}
    height={7}
    flexShrink={0}
    align="center"
    gap={1}
    // calc on the space vars, not arithmetic on scale keys: the scale skips 11
    // and 13, so a deep folder resolved to a raw pixel value and outdented.
    pl={`calc(var(--chakra-space-1) + ${depth} * var(--chakra-space-3))`}
    pr={3}
    borderRadius="lg"
    bg={isSelected ? "white" : "transparent"}
    boxShadow={isSelected ? "sm" : "none"}
    _hover={{ bg: isSelected ? "white" : "base.background" }}
  >
    {hasChildren && onToggleExpanded ? (
      <IconButton
        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${label}`}
        aria-expanded={isExpanded}
        icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
        size="xs"
        variant="tertiary"
        flexShrink={0}
        // The button theme draws the focus ring on aria-expanded for open menu
        // triggers. Here expanded means an open folder, so only real focus rings.
        _expanded={{
          boxShadow: "none",
          _focusVisible: { boxShadow: "outline" },
        }}
        onClick={onToggleExpanded}
      />
    ) : (
      <Box boxSize={5} flexShrink={0} />
    )}

    <Box
      as="button"
      type="button"
      aria-current={isSelected ? "true" : undefined}
      flex={1}
      minWidth={0}
      height="100%"
      display="flex"
      alignItems="center"
      textAlign="left"
      borderRadius="md"
      _focusVisible={{ boxShadow: "outline" }}
      onClick={onSelect}
    >
      <TextWithTooltip
        size="sm"
        color={isSelected ? "text.primary" : "text.secondary"}
        isTruncated
      >
        {label}
      </TextWithTooltip>
    </Box>
  </Row>
);
